import { FC } from "react";
import { useTranslation } from "react-i18next";
import {
  SPEED_UNIT_VALUES,
  SPEED_UNITS_OPTIONS,
} from "../admin-details/account/constants";
import { convertKmphTo, speedUnitSuffix } from "../../utils/distance";
import { useLoggedInUserData } from "../../utils/user";
import { DaySummary, formatDuration } from "./util/tripSummary";

// The at-a-glance figures, in one place: one trip, a whole day, or one
// vehicle's day on the multi-vehicle map. Three copies of this markup would
// drift the way the two maps already have.
//
// Violations deliberately absent: see VIOLATION_CODES in util/tripSummary.

type Props = {
  summary: DaySummary;
  // the per-trip card and the date roll-up sit at different sizes
  className?: string;
};

export const TripSummaryChips: FC<Props> = ({ summary, className }) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "vehicleMap.tripDetails",
  });

  const orgSpeedUnitValue = useLoggedInUserData(
    "speedUnits"
  ) as SPEED_UNIT_VALUES;
  const speedUnit =
    SPEED_UNITS_OPTIONS.find((option) => option.value === orgSpeedUnitValue)
      ?.unit ?? SPEED_UNITS_OPTIONS[0].unit;

  const countForDuration = (count: number, seconds: number) =>
    t("summary_count_for_duration", {
      total: count,
      duration: formatDuration(seconds),
    });

  const stats = [
    {
      label: t("summary_idling"),
      value: countForDuration(summary.idleCount, summary.idleSeconds),
    },
    {
      label: t("summary_stops"),
      value: countForDuration(summary.stoppedCount, summary.stoppedSeconds),
    },
    {
      label: t("summary_max_speed"),
      value: `${Math.round(
        convertKmphTo(speedUnit, summary.maxSpeedKph)
      )} ${speedUnitSuffix(speedUnit)}`,
    },
  ];

  return (
    <div className={`flex flex-wrap gap-x-3 gap-y-1 ${className ?? ""}`}>
      {stats.map((stat) => (
        <span
          key={stat.label}
          className="font-semibold text-[10px] leading-4 text-heading-gray666"
        >
          {stat.label}: <span className="text-heading-black">{stat.value}</span>
        </span>
      ))}
    </div>
  );
};

export default TripSummaryChips;
